import * as stylex from '@stylexjs/stylex'
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";

import AppShell, { type AppSection } from "./components/AppShell";
import MobileAppShell from "./components/MobileAppShell";
import CreateGroupDialog from "./components/CreateGroupDialog";
import CurrencyExchangeDialog from "./components/CurrencyExchangeDialog";
import ExpenseDialog from "./components/ExpenseDialog";
import ExpenseDetailDialog from "./components/ExpenseDetailDialog";
import ExpenseDetailMobilePage from './pages/expenses/ExpenseDetailMobilePage';
import EditPersonDialog from "./components/EditPersonDialog";
import PersonDialog from "./components/PersonDialog";
import SettlementDialog from "./components/SettlementDialog";
import Toast from "./components/Toast";
import DashboardPage from "./pages/dashboard/DashboardPage";
import DashboardMobilePage from "./pages/dashboard/DashboardMobilePage";
import GroupsPage from "./pages/groups/GroupsPage";
import GroupsMobilePage from "./pages/groups/GroupsMobilePage";
import GroupDetailPage from "./pages/groups/GroupDetailPage";
import GroupDetailMobilePage from "./pages/groups/GroupDetailMobilePage";
import PeoplePage from "./pages/people/PeoplePage";
import PeopleMobilePage from "./pages/people/PeopleMobilePage";
import PersonDetailPage from "./pages/people/PersonDetailPage";
import PersonDetailMobilePage from "./pages/people/PersonDetailMobilePage";
import ActivityPage from "./pages/activity/ActivityPage";
import ActivityMobilePage from "./pages/activity/ActivityMobilePage";
import LoansPage from "./pages/loans/LoansPage";
import LoansMobilePage from "./pages/loans/LoansMobilePage";
import LoginPage from "./pages/login/LoginPage";
import CreateAccountPage from "./pages/login/CreateAccountPage";
import ProfilePage from "./pages/profile/ProfilePage";
import ProfileMobilePage from "./pages/profile/ProfileMobilePage";
import AdminPage from "./pages/admin/AdminPage";
import AdminMobilePage from "./pages/admin/AdminMobilePage";
import GuestSharePage from "./pages/share/GuestSharePage";
import PersonSharePage from "./pages/share/PersonSharePage";
import PersonShareDialog from "./components/PersonShareDialog";
import useIsMobile from "./lib/useIsMobile";
import {
  AUTH_ACTIVITY_KEY,
  clearAuthSession,
  getStoredAuthToken,
  ledgerAPI,
  storeAuthSession,
  storeAuthToken,
  v1API,
  type Balance,
  type AdminAccount,
  type CreateCurrencyExchangeInput,
  type CreateExpenseInput,
  type CreateGroupInput,
  type CreatePersonInput,
  type CreateSettlementInput,
  type DatabaseSyncState,
  type ImportPreview,
  type Overview,
  type ProfileSettingsInput,
  type UpdateExpenseInput,
} from "./lib/ledger";
import DatabaseSyncDialog from "./components/DatabaseSyncDialog";
import { createPasskey, getPasskey } from "./lib/passkeys";


const styles = stylex.create({
  s0: {
    display: "flex",
    minHeight: "100vh",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8faf7",
    padding: "20px",
  },
  s1: {
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#e0e8de",
    backgroundColor: "#fff",
    paddingInline: "24px",
    paddingBlock: "28px",
    textAlign: "center",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
  },
  s2: {
    marginInline: "auto",
    display: "block",
    height: "32px",
    width: "32px",
    borderRadius: "9999px",
    borderWidth: 2,
    borderColor: "#d7e7d5",
    borderTopColor: "#4d9360",
  },
  s3: {
    marginTop: "16px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#4e5e51",
  },
  s4: {
    maxWidth: "28rem",
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#eadbd2",
    backgroundColor: "#fff",
    paddingInline: "24px",
    paddingBlock: "28px",
    textAlign: "center",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
  },
  s5: {
    marginInline: "auto",
    display: "flex",
    height: "40px",
    width: "40px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "9999px",
    backgroundColor: "#fff1eb",
    color: "#aa5625",
  },
  s6: {
    marginTop: "16px",
    fontSize: "18px",
    fontWeight: 600,
  },
  s7: {
    marginTop: "8px",
    fontSize: "13px",
    lineHeight: "1.25rem",
    color: "#737d75",
  },
  s8: {
    marginTop: "20px",
    height: "40px",
    borderRadius: "0.75rem",
    backgroundColor: "#438b57",
    paddingInline: "16px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#fff",
  },
})
type Overlay =
  | { type: "expense"; groupID?: string; personID?: string }
  | { type: "expense-detail"; expenseID: string }
  | { type: "person-edit"; personID: string }
  | { type: "group" }
  | { type: "person" }
  | { type: "settlement"; balance: Balance }
  | { type: "exchange"; personID: string; fromCurrency?: string }
  | { type: "person-share"; personID: string }
  | null;

const AUTH_IDLE_TIMEOUT_MS = 60 * 60 * 1000;
const AUTH_REFRESH_INTERVAL_MS = 10 * 60 * 1000;
const AUTH_ACTIVITY_CHECK_INTERVAL_MS = 30 * 1000;

function sectionForPath(path: string): AppSection {
  if (path.startsWith("/groups")) return "groups";
  if (path.startsWith("/people")) return "people";
  if (path.startsWith("/loans")) return "loans";
  if (path.startsWith("/activity")) return "activity";
  return "dashboard";
}

function LoadingState() {
  return (
    <main {...stylex.props(styles.s0)}>
      <div {...stylex.props(styles.s1)}>
        <span {...stylex.props(styles.s2)} />
        <p {...stylex.props(styles.s3)}>
          Loading your balances…
        </p>
      </div>
    </main>
  );
}

function ErrorState({
  error,
  onRetry,
}: {
  error: string;
  onRetry: () => void;
}) {
  return (
    <main {...stylex.props(styles.s0)}>
      <section {...stylex.props(styles.s4)}>
        <span {...stylex.props(styles.s5)}>
          !
        </span>
        <h1 {...stylex.props(styles.s6)}>
          We could not load SettleUp
        </h1>
        <p {...stylex.props(styles.s7)}>{error}</p>
        <button
          {...stylex.props(styles.s8)}
          onClick={onRetry}
          type="button"
        >
          Try again
        </button>
      </section>
    </main>
  );
}

export default function App() {
  const [location, navigate] = useLocation();
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [overlay, setOverlay] = useState<Overlay>(null);
  const [toast, setToast] = useState("");
  const [syncState, setSyncState] = useState<DatabaseSyncState | null>(null);
  const [adminAccounts, setAdminAccounts] = useState<AdminAccount[]>([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState("");
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const isMobile = useIsMobile();
  const lastActivityAtRef = useRef(0);
  const lastRefreshAtRef = useRef(0);
  const refreshInFlightRef = useRef<Promise<void> | null>(null);
  const guestShareToken = location.match(/^\/share\/([^/]+)$/)?.[1];
  const personShareToken = location.match(/^\/person-share\/([^/]+)$/)?.[1];
  const isAppPath = !guestShareToken && !personShareToken && location !== "/" && location !== "/create-account";
  const isAdminPage = location === "/admin";

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const overview = await ledgerAPI.getOverview();
      const token = window.localStorage.getItem("settleup.token");
      let currentUser = overview.currentUser;
      if (token) {
        try {
          const account = await v1API.me();
          currentUser = {
            ...currentUser,
            email: account.email,
            currency: account.defaultCurrency,
            exchangeRates: account.exchangeRates,
            role: account.role,
          };
          setSyncState(await v1API.getDatabaseSyncState());
        } catch {
          // Sync is an optional environment capability. A missing route or
          // non-operator account must not prevent the ledger from loading.
          setSyncState(null);
        }
      } else {
        setSyncState(null);
      }
      setData({ ...overview, currentUser });
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "The server is not available right now.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAppPath && !data && !loading && !error) {
      const timeout = window.setTimeout(() => {
        void load();
      }, 0);
      return () => window.clearTimeout(timeout);
    }
    return undefined;
  }, [data, error, isAppPath, load, loading]);

  useEffect(() => {
    const onAuthExpired = () => {
      clearAuthSession();
      setData(null);
      setSyncState(null);
      setError("");
      navigate("/");
    };
    window.addEventListener("settleup:auth-expired", onAuthExpired);
    return () =>
      window.removeEventListener("settleup:auth-expired", onAuthExpired);
  }, [navigate]);

  const logout = useCallback(() => {
    clearAuthSession();
    setData(null);
    setSyncState(null);
    navigate("/");
  }, [navigate]);

  useEffect(() => {
    const token = getStoredAuthToken();
    if (!isAppPath || !data || !token) return undefined;

    const storedActivityAt = Number(
      window.localStorage.getItem(AUTH_ACTIVITY_KEY),
    );
    const initialActivityAt =
      Number.isFinite(storedActivityAt) && storedActivityAt > 0
        ? storedActivityAt
        : Date.now();
    if (Date.now() - initialActivityAt >= AUTH_IDLE_TIMEOUT_MS) {
      const timeout = window.setTimeout(logout, 0);
      return () => window.clearTimeout(timeout);
    }

    lastActivityAtRef.current = initialActivityAt;
    lastRefreshAtRef.current = Date.now();

    const refreshSession = async () => {
      if (refreshInFlightRef.current) return refreshInFlightRef.current;
      if (Date.now() - lastActivityAtRef.current >= AUTH_IDLE_TIMEOUT_MS) {
        logout();
        return undefined;
      }

      const refreshPromise = v1API
        .refresh()
        .then((session) => {
          storeAuthToken(session.token);
          lastRefreshAtRef.current = Date.now();
        })
        .finally(() => {
          refreshInFlightRef.current = null;
        });
      refreshInFlightRef.current = refreshPromise;
      return refreshPromise;
    };

    const markActivity = () => {
      const now = Date.now();
      lastActivityAtRef.current = now;
      window.localStorage.setItem(AUTH_ACTIVITY_KEY, String(now));
      if (now - lastRefreshAtRef.current >= AUTH_REFRESH_INTERVAL_MS)
        void refreshSession().catch(() => undefined);
    };

    const activityEvents: Array<keyof WindowEventMap> = [
      "pointerdown",
      "keydown",
      "wheel",
      "touchstart",
    ];
    activityEvents.forEach((event) =>
      window.addEventListener(event, markActivity, { passive: true }),
    );
    const activityCheck = window.setInterval(() => {
      const now = Date.now();
      if (now - lastActivityAtRef.current >= AUTH_IDLE_TIMEOUT_MS) {
        logout();
      } else if (now - lastRefreshAtRef.current >= AUTH_REFRESH_INTERVAL_MS) {
        void refreshSession().catch(() => undefined);
      }
    }, AUTH_ACTIVITY_CHECK_INTERVAL_MS);

    return () => {
      activityEvents.forEach((event) =>
        window.removeEventListener(event, markActivity),
      );
      window.clearInterval(activityCheck);
    };
  }, [data, isAppPath, logout]);

  useEffect(() => {
    if (!isAdminPage || data?.currentUser.role !== "admin") {
      return undefined;
    }
    let active = true;
    const timeout = window.setTimeout(() => {
      if (!active) return;
      setAdminLoading(true);
      setAdminError("");
      void v1API.listAdminAccounts()
        .then((accounts) => {
          if (active) setAdminAccounts(accounts);
        })
        .catch((reason) => {
          if (active) {
            setAdminError(reason instanceof Error ? reason.message : "Could not load accounts.");
          }
        })
        .finally(() => {
          if (active) setAdminLoading(false);
        });
    }, 0);
    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [data?.currentUser.role, isAdminPage]);

  if (guestShareToken) {
    return <GuestSharePage token={guestShareToken} />;
  }
  if (personShareToken) {
    return <PersonSharePage token={personShareToken} />;
  }

  if (!isAppPath) {
    if (location === "/create-account") {
      return (
        <CreateAccountPage
          onSignIn={() => navigate("/")}
          onRegister={async (email, password, displayName, currency) => {
            const session = await v1API.register(
              email,
              password,
              displayName,
              currency,
            );
            storeAuthSession(session.token);
            navigate("/dashboard");
          }}
        />
      );
    }
    return (
      <LoginPage
        onSignIn={async (email, password) => {
          const session = await v1API.login(email, password);
          storeAuthSession(session.token);
          navigate("/dashboard");
        }}
        onPasskeySignIn={async () => {
          const ceremony = await v1API.passkeyLoginOptions();
          const credential = await getPasskey(ceremony.options);
          const session = await v1API.passkeyLoginVerify(ceremony.sessionId, credential);
          storeAuthSession(session.token);
          navigate("/dashboard");
        }}
        onCreateAccount={() => navigate("/create-account")}
      />
    );
  }
  if (loading || (!data && !error)) {
    return <LoadingState />;
  }
  if (!data) {
    return <ErrorState error={error} onRetry={() => void load()} />;
  }

  const active = sectionForPath(location);
  const groupID = location.match(/^\/groups\/([^/]+)$/)?.[1];
  const activeGroup = groupID
    ? data.groups.find((group) => group.id === groupID)
    : undefined;
  const personID = location.match(/^\/people\/([^/]+)$/)?.[1];
  const activePerson = personID
    ? data.people.find((person) => person.id === personID)
    : undefined;
  const isProfilePage = location === "/profile";

  async function createExpense(input: CreateExpenseInput) {
    const result = await ledgerAPI.createExpense(input);
    setData(result);
    setOverlay(null);
    setToast("Expense saved and balances updated.");
  }

  async function updateExpense(expenseID: string, input: UpdateExpenseInput) {
    await v1API.updateExpense(expenseID, input);
    setData(await ledgerAPI.getOverview());
    setOverlay(null);
    setToast("Expense updated and balances recalculated.");
  }

  async function reverseExpense(expenseID: string) {
    await v1API.reverseExpense(expenseID);
    setData(await ledgerAPI.getOverview());
    setOverlay(null);
    setToast("Expense reversed and balances recalculated.");
  }

  async function refreshAfterGuestSettlement() {
    setData(await ledgerAPI.getOverview());
    setToast("Payment confirmed and balances updated.");
  }

  async function previewSplitwiseImport(file: File): Promise<ImportPreview> {
    return v1API.previewSplitwiseImport(file);
  }

  async function commitSplitwiseImport(importID: string) {
    await v1API.commitImport(importID);
    setData(await ledgerAPI.getOverview());
    setToast("Splitwise data imported successfully.");
  }

  async function discardSplitwiseImport(importID: string) {
    await v1API.deleteImport(importID);
  }

  async function createGroup(input: CreateGroupInput) {
    const result = await ledgerAPI.createGroup(input);
    const newGroup = result.groups.at(-1);
    setData(result);
    setOverlay(null);
    setToast("Your group is ready for its first expense.");
    if (newGroup) {
      navigate(`/groups/${newGroup.id}`);
    }
  }

  async function createPerson(input: CreatePersonInput) {
    await v1API.createPerson(input);
    setData(await ledgerAPI.getOverview());
    setOverlay(null);
    setToast(`${input.displayName} was added to your circle.`);
  }

  async function updatePerson(personID: string, input: CreatePersonInput) {
    await v1API.updatePerson(personID, input);
    setData(await ledgerAPI.getOverview());
    setOverlay(null);
    setToast("Person details updated.");
  }

  async function saveProfileSettings(input: ProfileSettingsInput) {
    if (!data) {
      throw new Error("Your profile is not ready yet.");
    }
    await v1API.updateMe(input);
    await v1API.updatePerson(data.currentUser.id, {
      displayName: data.currentUser.name,
      email: input.email,
      avatar: input.avatar,
    });
    setData(await ledgerAPI.getOverview());
    setToast("Profile preferences saved.");
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    await v1API.updatePassword({ currentPassword, newPassword });
    setToast("Password changed successfully.");
  }

  async function registerPasskey() {
    const ceremony = await v1API.passkeyRegistrationOptions();
    const credential = await createPasskey(ceremony.options);
    await v1API.passkeyRegistrationVerify(ceremony.sessionId, credential);
    setToast("Passkey registered. You can use it the next time you sign in.");
  }

  async function reloadAdminAccounts() {
    setAdminLoading(true);
    setAdminError("");
    try {
      setAdminAccounts(await v1API.listAdminAccounts());
    } catch (reason) {
      setAdminError(reason instanceof Error ? reason.message : "Could not load accounts.");
    } finally {
      setAdminLoading(false);
    }
  }

  async function resetAdminAccountPassword(accountID: string, password: string) {
    await v1API.resetAdminAccountPassword(accountID, password);
    await reloadAdminAccounts();
  }

  async function revokeAdminAccountSessions(accountID: string) {
    await v1API.revokeAdminAccountSessions(accountID);
    await reloadAdminAccounts();
  }

  async function createSettlement(input: CreateSettlementInput) {
    const result = await ledgerAPI.createSettlement(input);
    setData(result);
    setOverlay(null);
    setToast("Payment recorded and balance updated.");
  }

  async function createCurrencyExchange(input: CreateCurrencyExchangeInput) {
    const result = await ledgerAPI.createCurrencyExchange(input);
    setData(result);
    setOverlay(null);
    setToast("Currency exchange recorded and balances updated.");
  }

  function navigateTo(section: AppSection) {
    navigate(`/${section === "dashboard" ? "dashboard" : section}`);
  }

  async function openDatabaseSync() {
    try {
      setSyncState(await v1API.getDatabaseSyncState());
    } catch {
      // Keep the last known capability; the dialog will surface a start error
      // if the server has become unavailable.
    }
    setSyncDialogOpen(true);
  }

  const page = isAdminPage && data.currentUser.role === "admin" ? (
    <AdminPage
      accounts={adminAccounts}
      error={adminError}
      loading={adminLoading}
      onResetPassword={resetAdminAccountPassword}
      onRetry={() => void reloadAdminAccounts()}
      onRevokeSessions={revokeAdminAccountSessions}
    />
  ) : isProfilePage ? (
    <ProfilePage
      onBack={() => navigate("/dashboard")}
      onChangePassword={changePassword}
      onSaveProfile={saveProfileSettings}
      onSyncProductionData={() => { void openDatabaseSync() }}
      onRegisterPasskey={registerPasskey}
      onOpenAdmin={() => navigate("/admin")}
      onPreviewSplitwiseImport={previewSplitwiseImport}
      onCommitSplitwiseImport={commitSplitwiseImport}
      onDiscardSplitwiseImport={discardSplitwiseImport}
      syncState={syncState}
      user={data.currentUser}
    />
  ) : activePerson ? (
    <PersonDetailPage
      data={data}
      onEditPerson={() =>
        setOverlay({ type: "person-edit", personID: activePerson.id })
      }
      onOpenExpense={(expenseID) =>
        setOverlay({ type: "expense-detail", expenseID })
      }
      onAddExpense={() =>
        setOverlay({ type: "expense", personID: activePerson.id })
      }
      onRecordExchange={(fromCurrency) =>
        setOverlay({
          type: "exchange",
          personID: activePerson.id,
          fromCurrency,
        })
      }
      onSettle={(balance) => setOverlay({ type: "settlement", balance })}
      onShare={() => setOverlay({ type: "person-share", personID: activePerson.id })}
      personID={activePerson.id}
      onBack={() => navigate("/people")}
    />
  ) : activeGroup ? (
    <GroupDetailPage
      data={data}
      group={activeGroup}
      onAddExpense={(nextGroupID) =>
        setOverlay({ type: "expense", groupID: nextGroupID })
      }
      onBack={() => navigate("/groups")}
      onSettle={(balance) => setOverlay({ type: "settlement", balance })}
    />
  ) : active === "groups" ? (
    <GroupsPage
      data={data}
      onAddExpense={(nextGroupID) =>
        setOverlay({ type: "expense", groupID: nextGroupID })
      }
      onCreateGroup={() => setOverlay({ type: "group" })}
      onOpenGroup={(nextGroupID) => navigate(`/groups/${nextGroupID}`)}
    />
  ) : active === "people" ? (
    <PeoplePage
      data={data}
      onAddPerson={() => setOverlay({ type: "person" })}
      onSettle={(balance) => setOverlay({ type: "settlement", balance })}
      onShare={(personID) => setOverlay({ type: "person-share", personID })}
      onOpenPerson={(id) => navigate(`/people/${id}`)}
    />
  ) : active === "loans" ? (
    <LoansPage data={data} />
  ) : active === "activity" ? (
    <ActivityPage
      data={data}
      onOpenExpense={(expenseID) =>
        setOverlay({ type: "expense-detail", expenseID })
      }
      onOpenGroup={(nextGroupID) => navigate(`/groups/${nextGroupID}`)}
    />
  ) : (
    <DashboardPage
      data={data}
      onAddExpense={(nextGroupID) =>
        setOverlay({ type: "expense", groupID: nextGroupID })
      }
      onCreateGroup={() => setOverlay({ type: "group" })}
      onOpenExpense={(expenseID) =>
        setOverlay({ type: "expense-detail", expenseID })
      }
      onOpenGroup={(nextGroupID) => navigate(`/groups/${nextGroupID}`)}
      onOpenPerson={(nextPersonID) => navigate(`/people/${nextPersonID}`)}
      onSettle={(balance) => setOverlay({ type: "settlement", balance })}
    />
  );

  const mobilePage = isAdminPage && data.currentUser.role === "admin" ? (
    <AdminMobilePage
      accounts={adminAccounts}
      error={adminError}
      loading={adminLoading}
      onResetPassword={resetAdminAccountPassword}
      onRetry={() => void reloadAdminAccounts()}
      onRevokeSessions={revokeAdminAccountSessions}
    />
  ) : isProfilePage ? (
    <ProfileMobilePage
      onBack={() => navigate("/dashboard")}
      onChangePassword={changePassword}
      onLogout={logout}
      onSaveProfile={saveProfileSettings}
      onSyncProductionData={() => { void openDatabaseSync() }}
      onRegisterPasskey={registerPasskey}
      onOpenAdmin={() => navigate("/admin")}
      onPreviewSplitwiseImport={previewSplitwiseImport}
      onCommitSplitwiseImport={commitSplitwiseImport}
      onDiscardSplitwiseImport={discardSplitwiseImport}
      syncState={syncState}
      user={data.currentUser}
    />
  ) : activePerson ? (
    <PersonDetailMobilePage
      data={data}
      onEditPerson={() =>
        setOverlay({ type: "person-edit", personID: activePerson.id })
      }
      onOpenExpense={(expenseID) =>
        setOverlay({ type: "expense-detail", expenseID })
      }
      onAddExpense={() =>
        setOverlay({ type: "expense", personID: activePerson.id })
      }
      onRecordExchange={(fromCurrency) =>
        setOverlay({
          type: "exchange",
          personID: activePerson.id,
          fromCurrency,
        })
      }
      onSettle={(balance) => setOverlay({ type: "settlement", balance })}
      onShare={() => setOverlay({ type: "person-share", personID: activePerson.id })}
      personID={activePerson.id}
      onBack={() => navigate("/people")}
    />
  ) : activeGroup ? (
    <GroupDetailMobilePage
      data={data}
      group={activeGroup}
      onAddExpense={(nextGroupID) =>
        setOverlay({ type: "expense", groupID: nextGroupID })
      }
      onBack={() => navigate("/groups")}
      onSettle={(balance) => setOverlay({ type: "settlement", balance })}
    />
  ) : active === "groups" ? (
    <GroupsMobilePage
      data={data}
      onAddExpense={(nextGroupID) =>
        setOverlay({ type: "expense", groupID: nextGroupID })
      }
      onCreateGroup={() => setOverlay({ type: "group" })}
      onOpenGroup={(nextGroupID) => navigate(`/groups/${nextGroupID}`)}
    />
  ) : active === "people" ? (
    <PeopleMobilePage
      data={data}
      onAddPerson={() => setOverlay({ type: "person" })}
      onSettle={(balance) => setOverlay({ type: "settlement", balance })}
      onShare={(personID) => setOverlay({ type: "person-share", personID })}
      onOpenPerson={(id) => navigate(`/people/${id}`)}
    />
  ) : active === "loans" ? (
    <LoansMobilePage data={data} />
  ) : active === "activity" ? (
    <ActivityMobilePage
      data={data}
      onOpenExpense={(expenseID) =>
        setOverlay({ type: "expense-detail", expenseID })
      }
      onOpenGroup={(nextGroupID) => navigate(`/groups/${nextGroupID}`)}
    />
  ) : (
    <DashboardMobilePage
      data={data}
      onCreateGroup={() => setOverlay({ type: "group" })}
      onOpenExpense={(expenseID) =>
        setOverlay({ type: "expense-detail", expenseID })
      }
      onOpenGroup={(nextGroupID) => navigate(`/groups/${nextGroupID}`)}
      onOpenPerson={(nextPersonID) => navigate(`/people/${nextPersonID}`)}
      onSettle={(balance) => setOverlay({ type: "settlement", balance })}
    />
  );

  const detailExpense =
    overlay?.type === "expense-detail"
      ? data.expenses.find((expense) => expense.id === overlay.expenseID)
      : undefined;
  const targetEnvironment =
    syncState?.environment === "development" ||
    syncState?.environment === "staging"
      ? syncState.environment
      : undefined;
  return (
    <>
      {isMobile ? (
        <MobileAppShell
          active={active}
          currentUser={data.currentUser}
          onAddExpense={() =>
            setOverlay({ type: "expense", groupID: activeGroup?.id })
          }
          onNavigate={navigateTo}
          onOpenProfile={() => navigate("/profile")}
        >
          {mobilePage}
        </MobileAppShell>
      ) : (
        <AppShell
          active={active}
          currentUser={data.currentUser}
          onAddExpense={() =>
            setOverlay({ type: "expense", groupID: activeGroup?.id })
          }
          onCreateGroup={() => setOverlay({ type: "group" })}
          onLogout={logout}
          onOpenProfile={() => navigate("/profile")}
          onNavigate={navigateTo}
        >
          {page}
        </AppShell>
      )}
      {overlay?.type === "person-edit" && (
        <EditPersonDialog
          person={data.people.find((person) => person.id === overlay.personID)!}
          onClose={() => setOverlay(null)}
          onSave={(input) => updatePerson(overlay.personID, input)}
        />
      )}
      {overlay?.type === "expense" && (
        <ExpenseDialog
          data={data}
          defaultGroupID={overlay.groupID}
          defaultPersonID={overlay.personID}
          onClose={() => setOverlay(null)}
          onSave={createExpense}
        />
      )}
      {overlay?.type === "exchange" && (
        <CurrencyExchangeDialog
          data={data}
          initialFromCurrency={overlay.fromCurrency}
          personID={overlay.personID}
          onClose={() => setOverlay(null)}
          onSave={createCurrencyExchange}
        />
      )}
      {detailExpense && (isMobile ? <ExpenseDetailMobilePage
        data={data} expense={detailExpense} onClose={() => setOverlay(null)}
        onReverse={() => reverseExpense(detailExpense.id)} onSave={(input) => updateExpense(detailExpense.id, input)}
        onSettlementConfirmed={refreshAfterGuestSettlement}
      /> :
        <ExpenseDetailDialog
          data={data}
          expense={detailExpense}
          onClose={() => setOverlay(null)}
          onReverse={() => reverseExpense(detailExpense.id)}
          onSave={(input) => updateExpense(detailExpense.id, input)}
          onSettlementConfirmed={refreshAfterGuestSettlement}
        />
      )}
      {overlay?.type === "group" && (
        <CreateGroupDialog
          data={data}
          onClose={() => setOverlay(null)}
          onSave={createGroup}
        />
      )}
      {overlay?.type === "person" && (
        <PersonDialog onClose={() => setOverlay(null)} onSave={createPerson} />
      )}
      {overlay?.type === "settlement" && (
        <SettlementDialog
          balance={overlay.balance}
          data={data}
          onClose={() => setOverlay(null)}
          onSave={createSettlement}
        />
      )}
      {overlay?.type === "person-share" && (() => {
        const person = data.people.find((item) => item.id === overlay.personID);
        return person ? <PersonShareDialog personID={person.id} personName={person.name} onClose={() => setOverlay(null)} onSettlementConfirmed={refreshAfterGuestSettlement} /> : null;
      })()}
      {syncDialogOpen && targetEnvironment && (
        <DatabaseSyncDialog
          initialJob={syncState?.activeJob}
          onClose={() => setSyncDialogOpen(false)}
          onCompleted={() => window.location.reload()}
          targetEnvironment={targetEnvironment}
        />
      )}
      {toast && <Toast message={toast} onDismiss={() => setToast("")} />}
    </>
  );
}
